/**
 * Which campaign link brought a visitor, so a new account can say so once.
 *
 * Campaign links carry utm_source / utm_medium / utm_campaign / utm_content
 * (or a short ?ref=). The first tagged arrival is kept in this browser; right
 * after sign-up the app sends it to /growth/attribution, and never again.
 */
import { apiClient } from '@/services/api'

const KEY = 'aa_first_touch'
const SENT = 'aa_first_touch_sent'

export interface FirstTouch {
  source?: string
  medium?: string
  campaign?: string
  content?: string
  landing?: string
  referrer?: string
}

function store(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null // private mode or blocked storage: no attribution, nothing breaks
  }
}

/** The tags of a URL, or null when it carries none. */
export function readTouch(href: string, referrer: string): FirstTouch | null {
  const url = new URL(href)
  const q = url.searchParams
  const touch: FirstTouch = {
    source: q.get('utm_source') || q.get('ref') || undefined,
    medium: q.get('utm_medium') || undefined,
    campaign: q.get('utm_campaign') || undefined,
    content: q.get('utm_content') || undefined,
  }
  let refHost: string | undefined
  try {
    refHost = referrer ? new URL(referrer).host : undefined
  } catch {
    refHost = undefined
  }
  // a visit from another site counts too, even without tags
  if (refHost && refHost !== url.host && !refHost.endsWith('myappacademia.com')) touch.referrer = refHost
  if (!touch.source && !touch.campaign && !touch.referrer) return null
  touch.landing = url.pathname
  return touch
}

/** Call once at start-up: keeps the first tagged arrival. */
export function captureFirstTouch(): void {
  const s = store()
  if (!s || s.getItem(KEY)) return
  const touch = readTouch(window.location.href, document.referrer)
  if (touch) s.setItem(KEY, JSON.stringify(touch))
}

/** Call when a user is signed in: sends the first touch once. */
export async function sendFirstTouch(): Promise<void> {
  const s = store()
  const raw = s?.getItem(KEY)
  if (!s || !raw || s.getItem(SENT)) return
  try {
    await apiClient.post('/growth/attribution', JSON.parse(raw))
    s.setItem(SENT, '1')
  } catch {
    /* try again next time the app opens */
  }
}
